import { DOCUMENT, Injectable, inject } from '@angular/core';
import { TOOLTIP_CONFIG } from '@shared/constants';

/** ng-bootstrap stamps this on the trigger for as long as its tooltip is open. */
const OPEN_TRIGGER = '[aria-describedby^="ngb-tooltip-"]';
const LISTENER = { capture: true, passive: true } as const;

/**
 * Dismisses `ngbTooltip` hints on touch devices (iPad, tablets, touch laptops), installed once from
 * `app.config.ts` — **no per-component or per-template wiring**.
 *
 * Tooltips open on `hover` (`TOOLTIP_CONFIG.triggers`), i.e. `mouseenter` → `mouseleave`. Tapping on a
 * touch screen fires the emulated `mouseenter`, but **no `mouseleave` until the user taps something
 * else** — the browser keeps the tapped element as the hovered one. So the hint opens on tap and then
 * sits there over the page: ng-bootstrap's own `autoClose` ignores clicks on the trigger itself, so only
 * a tap elsewhere ever clears it.
 *
 * This closes the gap by giving a tapped tooltip a short life of its own — it dismisses itself
 * `TOOLTIP_CONFIG.touchVisibleDuration` after it appears, and immediately on the next tap or scroll.
 * A real mouse moving on a hybrid device (iPad + trackpad) hands plain hover behaviour straight back.
 *
 * Notes:
 * - `touchstart` never fires on a mouse-only device, so this costs a desktop nothing but one listener.
 * - Tooltips are closed through the directive's own `mouseleave` trigger, never by removing the
 *   tooltip node, so `ngbTooltip` stays in charge of its own window ref and `aria-describedby`.
 * - `triggers="manual"` tooltips are untouched — they have no `mouseleave` trigger and belong to the
 *   component driving them.
 */
@Injectable({ providedIn: 'root' })
export class TouchTooltipService {
  private readonly document = inject(DOCUMENT);
  private installed = false;
  private touchMode = false;
  private watchingMouse = false;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  install(): void {
    if (this.installed) {
      return;
    }
    this.installed = true;
    this.document.addEventListener('touchstart', this.onTouchStart, LISTENER);
    // Capture, because `scroll` doesn't bubble out of the app's inner scroll containers.
    this.document.addEventListener('scroll', this.onScroll, LISTENER);
  }

  private readonly onTouchStart = (): void => {
    this.touchMode = true;
    this.watchMouse();
    // Anything open belongs to the previous tap. This tap's own `mouseenter` hasn't landed yet, so
    // clearing here drops the stale hint without suppressing the new one.
    this.dismiss();
    this.hideTimer = setTimeout(
      () => this.dismiss(),
      TOOLTIP_CONFIG.openDelay + TOOLTIP_CONFIG.touchVisibleDuration
    );
  };

  private readonly onScroll = (): void => {
    // A pending hide timer is also the "a tooltip may be open" flag — no tap, nothing to dismiss.
    if (this.touchMode && this.hideTimer !== null) {
      this.dismiss();
    }
  };

  private watchMouse(): void {
    if (this.watchingMouse) {
      return;
    }
    this.watchingMouse = true;
    this.document.addEventListener(
      'pointermove',
      (event: PointerEvent) => {
        if (this.touchMode && event.pointerType === 'mouse') {
          this.touchMode = false;
          this.clearHide();
        }
      },
      LISTENER
    );
  }

  private dismiss(): void {
    this.clearHide();
    for (const trigger of Array.from(this.document.querySelectorAll<HTMLElement>(OPEN_TRIGGER))) {
      trigger.dispatchEvent(new MouseEvent('mouseleave', { bubbles: false, cancelable: false }));
    }
  }

  private clearHide(): void {
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }
}
